import { Form, notification, Input, Button, Select, Row, Col, Badge, Typography, Divider, Space, Spin, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useImperativeHandle, useState } from "react";
import { getMFAByCurrentUser, getSoftwareToken, getTenantInfo, getTenantMFAInfoByCurrentUser, setupSoftwareToken, updateMFAByCurrentUser } from "@/apis";
import { useAuthContext } from "@/providers/auth-provider";
import { useResourceRootContext } from "@/layouts/ResourceContext";
import { forwardRef } from "react";

const { Text } = Typography;
export const MFAUserConfig = forwardRef((props, ref) => {
  const { state } = useResourceRootContext();
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const [form2] = Form.useForm();

  const MFA_SETTING_MAP = {
    "SOFTWARE_TOKEN_MFA": "app",
    "EMAIL_OTP": "email"
  }

  const isSwitch1Checked = Form.useWatch('app', form2);
  const codeValue = Form.useWatch('code', form2)

  const [loading, setLoading] = useState(false);
  const [base64String, setBase64String] = useState("");
  const [enabledMFAs, setEnabledMFAs] = useState([]);
  const [enableMFA, setEnableMFA] = useState(false);
  const [enableAPP, setEnableAPP] = useState(false);
  const [enableSMS, setEnableSMS] = useState(false);
  const [enableEmail, setEnableEmail] = useState(false);
  const [preferredMFA, setPreferredMFA] = useState("");
  const [configCode, setConfigCode] = useState(false);

  const [pendingValue, setPendingValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false); 

  const [errorString, setErrorString] = useState("");
  useEffect(() => {
    if(user.tenant != "__root__") {
      const getMFAByTenant = async() => {
        const result = await getTenantMFAInfoByCurrentUser();
        /**
         * "email_mfa": true,
        "mfa_config": "ON",
        "app_mfa": true,
        "sms_mfa": false
         */
        if(result) {
          setEnableMFA(result.mfa_config === "ON");
          setEnableAPP(result.app_mfa);
          setEnableSMS(result.sms_mfa);
          setEnableEmail(result.email_mfa);
          setEnabledMFAs(Object.keys(result).filter(key => result[key] === true));
        }
      }
      getMFAByTenant();
    }
  },[user.tenant]);

  
  useEffect(() => {
    const getCurrentUserMFA = async () => {
      const result = await getMFAByCurrentUser();
      setLoading(false);
      if(result && result.preferred_mfa) {
        setPreferredMFA(MFA_SETTING_MAP[result.preferred_mfa]);
      }
    }
    setLoading(true);
    getCurrentUserMFA();                                                        
  }, [user.id]);  

  useImperativeHandle(ref, () => ({
    onClose : () => {
      if(configCode)
        setConfigCode(false);
      if(errorString) {
        setErrorString("");
      }
      if(base64String)
        setBase64String("");
    }
  }));

  const updateItem = () => {
    const getST = async () => {
      const result = await getSoftwareToken();
      if(result.image_data_url) {
        setBase64String(result.image_data_url);
      }
    }
    getST();    
  }

  const steps = [
    {
      title: '1',
      content: 'Install a compatible application such as Google Authenticator, Duo Mobile, or Authy app on your mobile device or computer.',
    },
    {
      title: '2',
      content: 'Open your authenticator app, choose Show QR code on this page, then use the app to scan the code.',
    },
    {
      title: '3',
      content: 'Type MFA code below:',
    },
  ];

  const handleConfirm = async () => {
    setModalVisible(false);
    doPreferredChange(pendingValue);
  }

  const doPreferredChange = async (value) => {
    const req = Object.fromEntries(enabledMFAs.map(char => [char, true]));
    req.preferred_mfa = value;
    setLoading(true);
    const result = await updateMFAByCurrentUser(req);
    setLoading(false);
    if(result && "OK" === result.status) {
      setPreferredMFA(value);
      if(preferredMFA == "email" && configCode)
        setConfigCode(false);
      notification.success({
        description: 'Preferred MFA changed successfully!' 
      });
    } else {
      if(result?.value.indexOf("turn on SOFTWARE_TOKEN_MFA") != -1) {
        setErrorString("Please bind the app device first!");
        setConfigCode(true);
      }
      setPreferredMFA("");
      setPendingValue("");
    }
  }

  const handleCancel = () => {
    setModalVisible(false);
    setPendingValue("");
  }

  const selectPreferredMFA = (mfa) => {
    setPendingValue(mfa);
    setModalVisible(true);
  }

  const verifyCode = async() => {
    const result = await setupSoftwareToken({code: codeValue});
    if("OK" === result?.status) {
      notification.success({
        description: "Verify authentication code successfully!"
      })
      setConfigCode(false);
      if(errorString) {
        setErrorString("");
      }
      if(preferredMFA != "app") {
        doPreferredChange("app");
      }
    } else {
      notification.error({
        description: "Authentication code for device is not valid."
      })
    }
  }

  const isValid = () => {
    return /^\d{6}$/.test(codeValue);
  };

  return (
    <div>
      <Form form={form2}>
        <Form.Item label="User Preferred MFA" name="preferred">
          <Select 
            style={{width: '65%'}} 
            value={preferredMFA} 
            options={enabledMFAs.map((mfa) => ({"label": mfa.split("_")[0], "value": mfa.split("_")[0]}))} 
            onChange={selectPreferredMFA}
          />
          {loading && <Spin style={{marginLeft: "8px"}} size={'small'}/>} 
          {preferredMFA == "app" && <Button style={{marginLeft: '20px'}} onClick={() => setConfigCode(true)}>Assgin MFA Device</Button>}
          <div style={{'display': 'none'}}>{preferredMFA}</div>
        </Form.Item>
        {configCode && preferredMFA !== "email" && (
          <div>
            <Divider/>
            {errorString && <Typography.Text style={{color: 'red'}}>{errorString}</Typography.Text>}
            <Form.Item>
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                {steps.map((step, index) => (
                  <Col key={index} span={8} style={{ textAlign: 'center' }}>
                    <Badge
                      count={step.title}
                      style={{
                        backgroundColor: 'rgb(20,133,193)',
                        color: '#fff',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    />
                  </Col>
                ))}
              </Row>
              <Row gutter={16}>
                {steps.map((step, index) => {
                  if(index == 0) {
                    return (
                      <Col key={index} span={8} style={{ textAlign: 'center' }}>
                        <Text>{step.content}</Text>
                      </Col>
                    )
                  } else if(index == 1) {
                    return (
                      <Col key={index} span={8} style={{ textAlign: 'center' }}>
                        <Text>{step.content}</Text>
                        {!base64String ? (<div
                          style={{
                            width: '150px', 
                            height: '200px',
                            backgroundColor: '#f5f5f5', 
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: '20px auto',
                          }}
                        >
                          <Text style={{ fontSize: '15px', fontWeight: 'bold' , cursor: 'pointer', color: '#1890ff', }} onClick={updateItem}>
                            Show QR Code
                          </Text>
                        </div>) : (
                          <div>
                            <img style={{width: '260px', height: '260px'}} src={base64String}/>
                          </div>
                        )
                      }
                      </Col>
                    )
                  } else if(index == 2) {
                    return (
                      <Col key={index} span={8} style={{ textAlign: 'center' }}>
                        <Text>{step.content}</Text>
                        <Space style={{textAlign: 'right'}} direction="vertical">
                          <Form.Item name="code"
                            rules={[
                              {
                                pattern: /^\d{6}$/,
                                message: 'Input must be a 6 digits number!',
                              },
                              { required: true, message: 'Please input a 6 digits number!' }
                            ]}
                          >
                            <Input/>
                          </Form.Item>
                          <Button onClick={verifyCode} disabled={!isValid() || !base64String}>Verify</Button>
                        </Space>  
                      </Col>
                    )
                  }
                })}
              </Row>
            </Form.Item>
          </div>
        )}
      </Form>
      <Modal
        title="Preferred MFA"
        open={modalVisible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>Do you confirm change the preferred MFA to {pendingValue}?</p>
      </Modal>
    </div>
    
  );
});
